/**
 * Google Analytics 4 for the PROD admin app (app.closingvault.app).
 *
 * Off unless the build sets VITE_GA4_MEASUREMENT_ID (a public ID, passed only by the PROD
 * deploy job in .github/workflows/ci.yml): with it unset, nothing is injected, no Google
 * script loads and `trackSignUp` does nothing. The marketing site carries the same tag
 * (scripts/build-site.mjs); the linker below ties the two domains into one visit, so a
 * sign-up here is credited to the campaign that brought the visitor to the site.
 */

const MEASUREMENT_ID = /^G-[A-Z0-9]{4,20}$/;
const LINKER_DOMAINS = ['closingvault.app', 'app.closingvault.app'];
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;
const SENT_KEY_PREFIX = 'cv_ga_sign_up:';
// First-party cookie set by the marketing site (site/functions/_middleware.js) on
// .closingvault.app when a visitor arrives with utm_* tags: the site's UTMs are not on the
// sign-up URL, since the "Start free trial" buttons are plain links.
const UTM_COOKIE = 'cv_utm';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

function measurementId(): string | null {
  const id = (import.meta.env.VITE_GA4_MEASUREMENT_ID ?? '').trim();
  return MEASUREMENT_ID.test(id) ? id : null;
}

/**
 * Pages that never load the tag (ADR-0069 §Security review W12): the keys
 * console, where a recovery kit and its code are shown, and the unlock page,
 * where they are entered. Their paths also carry a strict CSP (public/_headers).
 */
const NO_ANALYTICS_PATHS = [/^\/platform\/keys(\/|$)/, /^\/unlock(\/|$)/];

export function analyticsAllowedOn(pathname: string): boolean {
  return !NO_ANALYTICS_PATHS.some((path) => path.test(pathname));
}

/**
 * True when the app reached a no-analytics page by a CLIENT-SIDE route change
 * while the tag is already loaded: the page must be reloaded as a full document
 * so the tag is gone and the page's strict CSP applies. Links to those pages are
 * full loads already (`reloadDocument`); this catches any other way in (a
 * redirect, the back button, a link added later).
 */
export function needsFullReloadFor(pathname: string): boolean {
  return typeof window.gtag === 'function' && !analyticsAllowedOn(pathname);
}

/** Loads gtag.js and configures it (cross-domain linker). Call once at start-up. */
export function initAnalytics(): void {
  const id = measurementId();
  if (!id || window.gtag) return;
  const layer: unknown[] = (window.dataLayer = window.dataLayer ?? []);
  // gtag.js reads the `arguments` object itself, not an array of them.
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    layer.push(arguments);
  };
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(script);
  window.gtag('js', new Date());
  window.gtag('config', id, { linker: { domains: LINKER_DOMAINS } });
}

/** The UTM parameters of a URL's query string, lowercased (empty ones left out). */
export function utmFromSearch(search: string): Record<string, string> {
  const params = new URLSearchParams(search);
  const out: Record<string, string> = {};
  for (const key of UTM_KEYS) {
    const value = params.get(key)?.trim().toLowerCase().slice(0, 100);
    if (value) out[key] = value;
  }
  return out;
}

/**
 * The UTM tags the marketing site left in the `cv_utm` cookie, run through the same cleaning as
 * a URL's. Anything missing or malformed gives `{}` (never throws).
 */
export function utmFromCookie(cookies: string = safeCookies()): Record<string, string> {
  try {
    const raw = cookies
      .split(';')
      .map((part) => part.trim())
      .find((part) => part.startsWith(UTM_COOKIE + '='));
    if (!raw) return {};
    return utmFromSearch(decodeURIComponent(raw.slice(UTM_COOKIE.length + 1)));
  } catch {
    return {};
  }
}

function safeCookies(): string {
  try {
    return document.cookie;
  } catch {
    return '';
  }
}

const sentThisPage = new Set<string>();

/**
 * Reports the `sign_up` event for a trial account whose email has just been confirmed.
 * Once per account: remembered in this browser (and in memory when storage is blocked),
 * so a refresh or a re-render cannot report it twice. `search` is the sign-up page's
 * query string; its UTM parameters ride along as event parameters. When that URL has none
 * (the normal case: visitors come from the marketing site), the `cv_utm` cookie's tags are used.
 */
export function trackSignUp(email: string, search: string): void {
  if (!measurementId() || !window.gtag) return;
  const key = SENT_KEY_PREFIX + email.trim().toLowerCase();
  if (sentThisPage.has(key)) return;
  sentThisPage.add(key);
  try {
    if (window.localStorage.getItem(key)) return;
    window.localStorage.setItem(key, '1');
  } catch {
    /* storage unavailable: the in-memory guard still holds for this page */
  }
  const fromUrl = utmFromSearch(search);
  const utm = Object.keys(fromUrl).length ? fromUrl : utmFromCookie();
  window.gtag('event', 'sign_up', { method: 'free_trial', ...utm });
}
